<script>
	import { onMount } from "svelte";
	import { logApiAll as all, menu, menuSub, menuSubSub } from "../../stores";
	import { Dates } from "../../utils/date";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import "@andypf/json-viewer";

	// 상수 정의
	const BASE_URL = "/log/api";
	const PAGE_SIZES = [50, 100, 150, 200];
	const METHOD_COLOR = {
		GET: "#28a745",
		POST: "#007bff",
		PUT: "#ffc107",
		DELETE: "#dc3545",
	};

	// 상태 관리
	let filteredData = [];
	let currentPage = 1;
	let totalPages = 1;
	let totalItems = 0;
	let pageSize = 50;
	let isLoading = false;

	// 상세 팝업
	let selectedItem = null;
	let showDetailModal = false;

	// 검색 조건
	let logType = "latest";
	let method = "ALL";
	let keywordUser = "";
	let keywordLog = "";
	let startTm = "";
	let endTm = "";

	// 정렬 관련
	let sortField = "";
	let sortOrder = "DESC";

	// 검색 실행
	const handleSearch = () => {
		currentPage = 1;
		fnSearch();
	};

	// 검색어 초기화
	const handleReset = () => {
		logType = "latest";
		method = "ALL";
		keywordUser = "";
		keywordLog = "";
		startTm = "";
		endTm = "";
		sortField = "";
		sortOrder = "DESC";
		currentPage = 1;
		fnSearch();
	};

	// 모달 토글 함수
	const toggleDetailModal = (item) => {
		showDetailModal = !showDetailModal;
		if (!showDetailModal) {
			// 모달 닫을 때 입력값 초기화
			selectedItem = null;
		} else {
			selectedItem = item;
		}
	};

	const fnSearch = async () => {
		const params = new URLSearchParams({
			logType: logType.trim(),
			method: method.trim() || "ALL",
			keywordUser: keywordUser.trim() || "",
			keywordLog: keywordLog.trim() || "",
			startTm: startTm.trim() || "",
			endTm: endTm.trim() || "",
			sort: sortField.trim() || "",
			ord: sortOrder.trim() || "",
		});
		const url = `${BASE_URL}/${pageSize}/${currentPage}?${params.toString()}`;
		await all.fetch(url);
	};

	// 정렬 처리 함수
	const handleSort = (field) => {
		if (sortField === field) {
			// 같은 필드를 클릭하면 정렬 방향 토글
			sortOrder = sortOrder === "ASC" ? "DESC" : "ASC";
		} else {
			// 다른 필드를 클릭하면 해당 필드로 내림차순 정렬
			sortField = field;
			sortOrder = "DESC";
		}
		currentPage = 1;
		fnSearch();
	};

	// 정렬 상태 확인
	const getSortClass = (field) => {
		if (sortField !== field) return "sortable";
		return sortOrder === "ASC" ? "sortable sort-asc" : "sortable sort-desc";
	};

	// 드롭다운 변경 시 즉시 반영
	const handleDropdownChange = () => {
		currentPage = 1;
		fnSearch();
	};

	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value); // pageSize 값 업데이트
		currentPage = 1;
		fnSearch();
	};

	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fnSearch();
	};

	// 날짜 계산 함수
	const getDateRange = () => {
		const today = new Date();
		const sevenDaysAgo = new Date();
		sevenDaysAgo.setDate(today.getDate() - 7);

		const formatDate = (date) => {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			return `${year}-${month}-${day}`;
		};

		return {
			start: formatDate(sevenDaysAgo),
			end: formatDate(today),
		};
	};

	$: {
		const data = $all?.data || {};
		filteredData = data.list || [];
		totalPages = data.totalPage;
		totalItems = data.total || 0;
	}

	// 키보드 이벤트 처리
	const handleKeyPress = (event) => {
		if (event.key === "Enter") {
			handleSearch();
		}
	};

	onMount(() => {
		menu.set("로그 관리");
		menuSub.set("API 호출 로그");
		menuSubSub.set("");
		fnSearch();
	});
</script>

<div class="card mb-4">
	<!-- 검색 영역 -->
	<div class="card-header bg-light border-bottom" style="padding: 1.25rem;">
		<div class="row g-3">
			<!-- 첫 번째 줄: 드롭다운 및 안내 -->
			<div class="col-12">
				<div class="row align-items-center">
					<div class="col-auto">
						<div class="d-flex align-items-center gap-3">
							<div class="d-flex align-items-center gap-2">
								<label for="logTypeSelect" class="form-label mb-0 fw-semibold text-muted" style="font-size: 12px; min-width: 50px;">
									DB 선택
								</label>
								<select
									id="logTypeSelect"
									class="form-select form-select-sm"
									style="width: 110px;"
									bind:value={logType}
									on:change={handleDropdownChange}
								>
									<option value="latest">최신 DB</option>
									<option value="">백업 DB</option>
								</select>
							</div>
							<div class="d-flex align-items-center gap-2">
								<label for="methodSelect" class="form-label mb-0 fw-semibold text-muted" style="font-size: 12px; min-width: 45px;">
									Method
								</label>
								<select
									id="methodSelect"
									class="form-select form-select-sm"
									style="width: 110px;"
									bind:value={method}
									on:change={handleDropdownChange}
								>
									<option value="ALL">전체</option>
									<option value="GET">GET</option>
									<option value="POST">POST</option>
									<option value="PUT">PUT</option>
									<option value="DELETE">DELETE</option>
								</select>
							</div>
						</div>
					</div>
					<div class="col">
						<div class="alert alert-info mb-0 py-2 px-3" style="font-size: 11px; border-radius: 6px;">
							<i class="bx bx-info-circle me-1" style="font-size: 12px;"></i>
							<strong>최신 DB</strong>
							: {getDateRange().start} ~ {getDateRange().end} 데이터 •
							<strong>백업 DB</strong>
							: 이전 데이터
						</div>
					</div>
				</div>
			</div>

			<!-- 두 번째 줄: 검색 필드 -->
			<div class="col-12">
				<div class="row align-items-end g-2">
					<div class="col-auto">
						<label for="keywordUserInput" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">회원 이메일</label>
						<input
							id="keywordUserInput"
							type="text"
							class="form-control form-control-sm"
							placeholder="예: user@example.com"
							bind:value={keywordUser}
							on:keypress={handleKeyPress}
							style="width: 180px;"
						/>
					</div>
					<div class="col-auto">
						<label for="keywordLogInput" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">엔드포인트</label>
						<input
							id="keywordLogInput"
							type="text"
							class="form-control form-control-sm"
							placeholder="예: /api/user/login"
							bind:value={keywordLog}
							on:keypress={handleKeyPress}
							style="width: 180px;"
						/>
					</div>
					<div class="col-auto">
						<label for="startTmInput" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">시작 시간</label>
						<input
							id="startTmInput"
							type="datetime-local"
							class="form-control form-control-sm"
							bind:value={startTm}
							style="width: 170px;"
						/>
					</div>
					<div class="col-auto">
						<label for="endTmInput" class="form-label mb-1" style="font-size: 12px; color: #6c757d;">종료 시간</label>
						<input id="endTmInput" type="datetime-local" class="form-control form-control-sm" bind:value={endTm} style="width: 170px;" />
					</div>
					<div class="col-auto">
						<div class="d-flex gap-2">
							<button class="btn btn-primary btn-sm px-3" on:click={handleSearch} disabled={isLoading}>
								<i class="bx bx-search me-1"></i>
								검색
							</button>
							<button class="btn btn-outline-secondary btn-sm px-3" on:click={handleReset}>
								<i class="bx bx-refresh me-1"></i>
								초기화
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="table-responsive text-nowrap">
		<div class="ms-auto" style="padding: 5px;">
			<TableToolbar {totalItems} {currentPage} {totalPages} {pageSize} pageSizes={PAGE_SIZES} onPageSizeChange={handlePageSizeChange} />
		</div>
		<div class="fade-container">
			<div class="table-container">
				<table class="custom-table">
					<thead>
						<tr style="text-align:center">
							<th width="80">No</th>
							<th width="40" class={getSortClass("id_user")} on:click={() => handleSort("id_user")}>IDUser</th>
							<th width="40">Email</th>
							<th width="40" class={getSortClass("method")} on:click={() => handleSort("method")}>Method</th>
							<th width="40" class={getSortClass("method_url")} on:click={() => handleSort("method_url")}>MethodUrl</th>
							<th width="40" class={getSortClass("res_result_code")} on:click={() => handleSort("res_result_code")}>결과코드</th>
							<th width="*">결과</th>
							<th width="40" class={getSortClass("duration")} on:click={() => handleSort("duration")}>실행시간</th>
							<th width="40" class={getSortClass("created_at")} on:click={() => handleSort("created_at")}>생성일시</th>
						</tr>
					</thead>
					<tbody>
						{#if isLoading}
							<tr>
								<td colspan="9" class="text-center py-4">
									<div class="spinner-border spinner-border-sm me-2" role="status"></div>
									로딩 중...
								</td>
							</tr>
						{:else if filteredData.length === 0}
							<tr>
								<td colspan="7" class="text-center py-4" style="font-size: 12px;">데이터가 없습니다</td>
							</tr>
						{:else}
							{#each filteredData as item, index}
								<tr
									class={index % 2 == 0 ? "even-model" : "odd-model"}
									style="text-align:center; font-size: 12px;"
									on:click={() => toggleDetailModal(item)}
								>
									<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
									<td>{item.idUser}</td>
									<td>{item.email || "UNKNOWN"}</td>
									<td><b style={`color:${METHOD_COLOR[item.method]};`}>{item.method}</b></td>
									<td>{item.methodUrl}</td>
									<td>{item.resResultCode}</td>
									<td>{item.resResultDesc}</td>
									<td>{item.duration}</td>
									<td>{Dates.defaultConvertFull(item.createdAt)}</td>
								</tr>
							{/each}
						{/if}
					</tbody>
				</table>
			</div>
		</div>
		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
	<style>
		.table-container {
			padding: 0 15px;
			width: 100%;
		}
		.custom-table {
			border-collapse: collapse;
			border: 1px solid #ddd;
			margin-bottom: 5px;
			font-family: "Gulim";
			font-size: 11px;
			box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
			width: 100%;
		}

		/* 헤더 스타일 */
		.custom-table thead tr {
			text-align: center;
			background-color: #f2f2f2;
			border-bottom: 2px solid #ddd;
		}

		/* 셀 공통 스타일 */
		.custom-table th,
		.custom-table td {
			padding: 4px;
			border: 1px solid #ddd;
			text-align: center;
			font-size: 11px;
		}

		/* 짝수/홀수 행 스타일 */
		.even-model {
			background-color: #ffffff;
			padding: 4px;
			border: 1px solid #ddd;
			font-size: 11px;
		}

		.odd-model {
			background-color: #f9f9f9;
			padding: 4px;
			border: 1px solid #ddd;
			font-size: 11px;
		}

		/* 호버 효과 */
		.custom-table tr:hover {
			background-color: #f0f8ff !important;
			cursor: pointer;
		}

		/* 정렬 가능한 헤더 스타일 */
		.sortable {
			cursor: pointer;
			user-select: none;
			transition: background-color 0.2s;
			position: relative;
		}

		.sortable:hover {
			background-color: #e6e6e6 !important;
		}

		/* 정렬 아이콘 */
		.sort-asc::after {
			content: " ▲";
			font-size: 10px;
			color: #666;
		}

		.sort-desc::after {
			content: " ▼";
			font-size: 10px;
			color: #666;
		}
	</style>
</div>

<!-- 모달 영역 -->
{#if showDetailModal}
	<div class="modal-overlay">
		<div class="modal fade {showDetailModal ? 'show' : ''}" style="display: block;" tabindex="-1" aria-modal="true" role="dialog">
			<div class="modal-dialog modal-dialog-centered modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							{`${selectedItem.methodUrl} - ${selectedItem.email || "UNKNOWN"} : ${Dates.defaultConvertFull(selectedItem.createdAt)}`}
						</h5>
						<button type="button" class="btn-close" on:click={() => toggleDetailModal("")} />
					</div>
					<div class="modal-body">
						<!-- 요청 헤더 정보 -->
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center">
								<i class="bx bx-info-circle me-2"></i>
								요청 헤더 정보
							</h6>
							<div class="row mb-2">
								<div class="col-3 fw-bold">AcceptService</div>
								<div class="col-9 log-text long-text">{selectedItem.reqHeaderAcceptService}</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">DeviceInfo</div>
								<div class="col-9 log-text long-text">{selectedItem.reqHeaderDeviceInfo}</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">WearableInfo</div>
								<div class="col-9 log-text long-text">{selectedItem.reqHeaderWearableInfo}</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">PackageInfo</div>
								<div class="col-9 log-text long-text">{selectedItem.reqHeaderPackageInfo}</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">LocaleInfo</div>
								<div class="col-9 log-text long-text">{selectedItem.reqHeaderLocaleInfo}</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">UserAgent</div>
								<div class="col-9 log-text long-text">{selectedItem.reqHeaderUserAgent}</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">ContentType</div>
								<div class="col-9 log-text long-text">{selectedItem.reqHeaderContentType}</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">Authorization</div>
								<div class="col-9 log-text long-text">{selectedItem.reqHeaderAuthorization}</div>
							</div>
						</div>

						<!-- 요청 정보 -->
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center">
								<i class="bx bx-log-in-circle me-2"></i>
								요청 정보
							</h6>
							<div class="row mb-2">
								<div class="col-3 fw-bold">Method</div>
								<div class="col-9 log-text long-text">
									<b style={`color:${METHOD_COLOR[selectedItem.method]};`}>{selectedItem.method}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">MethodUrl</div>
								<div class="col-9 log-text long-text">
									<b>{selectedItem.methodUrl}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">ClientIP</div>
								<div class="col-9 log-text long-text">
									<b>{selectedItem.clientIP}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">IDUser</div>
								<div class="col-9 log-text long-text">
									<b>{selectedItem.idUser}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">Email</div>
								<div class="col-9 log-text long-text">
									<b>{selectedItem.email}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">ReqBody</div>
								<div class="col-9 log-text">
									<andypf-json-viewer show-data-types="false" show-toolbar="true" data={selectedItem.reqBody}></andypf-json-viewer>
								</div>
							</div>
						</div>

						<!-- 응답 정보 -->
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center">
								<i class="bx bx-log-out-circle me-2"></i>
								응답 정보
							</h6>
							<div class="row mb-2">
								<div class="col-3 fw-bold">ResultCode</div>
								<div class="col-9 log-text long-text">
									<b>{selectedItem.resResultCode}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">ResultDesc</div>
								<div class="col-9 log-text long-text">
									<b>{selectedItem.resResultDesc}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">Duration</div>
								<div class="col-9 log-text long-text">
									<b>{selectedItem.duration}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">CreatedAt</div>
								<div class="col-9 log-text long-text">
									<b>{Dates.defaultConvertFull(selectedItem.createdAt)}</b>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-3 fw-bold">ResBody</div>
								<div class="col-9 log-text">
									<andypf-json-viewer show-data-types="false" show-toolbar="true" data={selectedItem.resData} />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade show" />
		<style>
			.log-text {
				font-family: "Consolas", monospace;
				font-size: 11px;
				background-color: #f1f5f9;
				padding: 2px 4px;
				border-radius: 2px;
				max-width: 100%;
			}
			.long-text {
				word-wrap: break-word;
				overflow-wrap: break-word;
				word-break: break-all;
				white-space: pre-wrap;
			}
		</style>
	</div>
{/if}
